import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Collapse, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import MultiLineChart, { ChartSeries } from './charts/MultiLineChart';
import { patientEvolutionsService, TherapyGoalSummary as GoalSummary } from '../services/patientEvolutionsService';
import { colors } from '../theme/designSystem';

/**
 * Acordeão "Ver dados do objetivo" do formulário de evolução (RN-542e): o que o objetivo do
 * plano declara (meta, métrica, justificativa, régua de onde parte e aonde quer chegar,
 * sessões) e o gráfico das notas dadas nas evoluções, por data da sessão, contra a linha da
 * meta e a do estado inicial. Carrega sob demanda, na primeira abertura, e recarrega ao
 * trocar de objetivo com o painel aberto.
 */
interface Props {
  patientId: string;
  planId: string;
}

const EMPTY = '—';

const formatIsoDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const shortDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}`;

const Row: React.FC<{ label: string; value: string | null | undefined; wide?: boolean }> = ({ label, value, wide }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0, gridColumn: wide ? '1 / -1' : 'auto' }}>
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: colors.textPrimary, whiteSpace: 'nowrap' }}>{label}:</Typography>
    <Typography sx={{ fontSize: '0.82rem', color: colors.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}>
      {value && value.trim() ? value : EMPTY}
    </Typography>
  </Box>
);

/** Régua de 0 a 10: de onde o objetivo parte (estado atual), a última nota e a meta. */
const Ruler: React.FC<{ current: number | null; last: number | null; target: number | null }> = ({ current, last, target }) => {
  const pct = (v: number) => `${(Math.max(0, Math.min(10, v)) / 10) * 100}%`;
  return (
    <Box sx={{ gridColumn: '1 / -1', mt: 0.5 }}>
      <Box sx={{ position: 'relative', height: 8, borderRadius: 4, backgroundColor: '#e9ecef', mx: 0.5 }}>
        {current !== null && target !== null && (
          <Box sx={{ position: 'absolute', left: pct(Math.min(current, target)), width: `calc(${pct(Math.abs(target - current))})`, top: 0, bottom: 0, backgroundColor: '#cfe9ec', borderRadius: 4 }} />
        )}
        {current !== null && (
          <Box title={`Estado atual ${current}`} sx={{ position: 'absolute', left: pct(current), top: -3, width: 14, height: 14, ml: '-7px', borderRadius: '50%', border: '2px solid #6c757d', backgroundColor: '#fff' }} />
        )}
        {target !== null && (
          <Box title={`Meta ${target}`} sx={{ position: 'absolute', left: pct(target), top: -3, width: 14, height: 14, ml: '-7px', borderRadius: '50%', backgroundColor: colors.success }} />
        )}
        {last !== null && (
          <Box title={`Última nota ${last}`} sx={{ position: 'absolute', left: pct(last), top: -3, width: 14, height: 14, ml: '-7px', borderRadius: '50%', backgroundColor: colors.primary }} />
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
        <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>Estado atual: <strong>{current ?? EMPTY}</strong></Typography>
        <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>Última nota: <strong style={{ color: colors.primary }}>{last ?? EMPTY}</strong></Typography>
        <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>Meta: <strong style={{ color: colors.success }}>{target ?? EMPTY}</strong></Typography>
      </Box>
    </Box>
  );
};

const TherapyGoalSummary: React.FC<Props> = ({ patientId, planId }) => {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<GoalSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !planId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    patientEvolutionsService.getPlanSummary(patientId, planId)
      .then((data) => { if (!cancelled) setSummary(data); })
      .catch(() => { if (!cancelled) setError('Não foi possível carregar os dados do objetivo.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, patientId, planId]);

  const history = summary?.history ?? [];
  const last = history.length > 0 ? history[history.length - 1].scale : null;
  const labels = history.map(h => h.sessionDate);
  const series: ChartSeries[] = summary ? [
    { name: 'Nota da sessão', color: colors.primary, values: history.map(h => h.scale) },
    ...(summary.targetScale !== null ? [{ name: 'Meta', color: colors.success, values: history.map(() => summary.targetScale), dashed: true }] : []),
    ...(summary.currentScale !== null ? [{ name: 'Estado inicial', color: '#adb5bd', values: history.map(() => summary.currentScale), dashed: true }] : []),
  ] : [];

  const period = summary
    ? [summary.periodTitle, summary.initialDate && summary.finalDate
      ? `${formatIsoDate(summary.initialDate)} a ${formatIsoDate(summary.finalDate)}`
      : null].filter(Boolean).join(' · ')
    : '';

  return (
    <Box sx={{ mt: -0.5 }}>
      <Button
        size="small"
        onClick={() => setOpen(v => !v)}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        sx={{ textTransform: 'none', color: colors.primary, px: 0.5, minHeight: 32 }}
      >
        {open ? 'Ocultar dados do objetivo' : 'Ver dados do objetivo'}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ border: `1px solid ${colors.border}`, borderRadius: '8px', backgroundColor: '#f8f9fa', p: 2, mt: 0.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: colors.primary }} />
            </Box>
          ) : error ? (
            <Typography sx={{ fontSize: '0.85rem', color: colors.error }}>{error}</Typography>
          ) : summary && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: colors.textPrimary, overflowWrap: 'anywhere' }}>
                  {summary.title}
                </Typography>
                {(summary.isConcluded || summary.isExpired) && (
                  <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff', backgroundColor: '#adb5bd', borderRadius: '10px', px: 1, py: 0.2 }}>
                    {summary.isConcluded ? 'Concluído' : 'Vencido'}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, columnGap: 3, rowGap: 0.75 }}>
                <Row label="Período" value={period} wide />
                <Row label="Meta" value={summary.goal} wide />
                <Row label="Métrica" value={summary.metric} wide />
                <Row label="Justificativa" value={summary.justify} wide />
                <Row label="Situação" value={summary.status} />
                <Row label="Prioridade" value={summary.priority} />
                <Row label="Sessões" value={`${summary.sessionsDone} realizada${summary.sessionsDone === 1 ? '' : 's'}${summary.sessionsQuantity !== null ? ` de ${summary.sessionsQuantity} prevista${summary.sessionsQuantity === 1 ? '' : 's'}` : ''}`} />
                <Row label="Avaliações" value={`${history.length} com nota`} />
                <Ruler current={summary.currentScale} last={last} target={summary.targetScale} />
              </Box>

              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: colors.textPrimary, mt: 2, mb: 0.5 }}>
                Evolução do objetivo por sessão
              </Typography>
              {history.length === 0 ? (
                <Typography sx={{ fontSize: '0.82rem', color: colors.textSecondary }}>
                  Nenhuma nota registrada ainda para este objetivo. A primeira aparece aqui assim que uma evolução o avaliar.
                </Typography>
              ) : (
                <MultiLineChart
                  labels={labels}
                  series={series}
                  height={220}
                  showValues
                  valueFormatter={(v) => String(Math.round(v))}
                  labelText={shortDate}
                />
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default TherapyGoalSummary;
